/* eslint-disable complexity */
import { captureMessage } from '@sentry/browser';
import api from 'Cloud/Application/api';
import { logger } from 'lib/logger';
import { clone } from 'ramda';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { getExtension, getItemNameWithoutExt, getNameById, getParent } from 'reactApp/modules/file/utils';
import { addFilesSuccess, removeFileSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { ISnackbarWordDeclination, snackbarMessage } from 'reactApp/modules/modifying/modifying.constants';
import { EModifyReason, ESnackbarType } from 'reactApp/modules/modifying/modifying.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { folderCloudPath } from 'reactApp/modules/uploadList/uploadList.helpers';
import { UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import { store } from 'reactApp/store';
import { EConflictMode } from 'reactApp/types/EConflictMode';
import { filesPlural, foldersPlural } from 'reactApp/utils/pluralHelpers';
import { truncateTextMiddle } from 'reactApp/utils/textHelpers';

const urlBuilder = new UrlBuilder();

export const addFolderHelper = ({
    item,
    storage,
    parent,
    reason = EModifyReason.add,
    skipXhr = false,
    conflictMode = EConflictMode.RENAME,
}) => {
    function _success(item) {
        if ((item.id || {}).apiStatus === 'faked') {
            // CLOUDWEB-5028
            item.id = item.id.id;
        }

        item.parent = parent;
        item.size = item.__size || item.size || 0;

        if (reason !== EModifyReason.rename) {
            store.dispatch(
                addFilesSuccess({
                    items: [
                        {
                            ...clone(item),
                            kind: 'folder',
                            name: item.name || item.id.split('/').pop(),
                            count: {
                                folders: 0,
                                files: 0,
                            },
                            home: item.home || (storage !== EStorageType.public ? item.id : undefined),
                        },
                    ],
                    parent,
                    storage,
                    reason,
                })
            );
        }

        return item;
    }

    if (typeof item === 'string') {
        item = {
            name: item,
            id: `${parent}/${item}`,
        };
    }

    if (!skipXhr && item.name) {
        item.name = item.name.replace(/\//g, '_'); // Сейчас просто заменяем. Но, в будущем, должно проверяться на стороне API и выдавать ошибку
    }

    if (skipXhr) {
        return _success(item);
    }
    return new Promise((resolve, reject) => {
        const params = {
            home: parent + (parent === '/' ? '' : '/') + item.name,
            conflict: conflictMode,
        };

        api.folder
            .add(params)
            .done(function (added) {
                resolve(_success({ id: added }));
            })

            .fail(function (errors) {
                reject(errors);
            });
    });
};

export const addFileHelper = ({ item, parent, skipXhr, storage, conflictMode, reason }) => {
    function _success(item) {
        if (item.mtime === undefined) {
            item.mtime = Date.now() / 1000 || undefined;
        }

        store.dispatch(removeFileSuccess({ ids: [item.id], reason }));

        store.dispatch(
            addFilesSuccess({
                parent,
                storage,
                items: [
                    {
                        ...item,
                        name: item.name || item.nameWithoutExt,
                        kind: 'file',
                    },
                ],
                reason,
            })
        );

        return item;
    }

    if (skipXhr) {
        return _success(item);
    }

    return new Promise((resolve, reject) => {
        const params = {
            home: `${parent}/${item.name}`,
            hash: item.hash,
            size: item.size,
            conflict: conflictMode || 'rename',
        };

        api.file
            .add(params)
            .done(function (id) {
                // TODO: remove regexp after CLOUDPRL-647
                id = id.replace(/\/{2,}/g, '/');

                resolve(
                    _success({
                        ...item,
                        id,
                        name: id.split('/').pop(),
                    })
                );
            })
            .fail(function (errors, status, response) {
                // @ts-ignore
                reject(errors, status);

                if (response.isInsufficient()) {
                    captureMessage('OVERQUOTED for ADD FILE', {
                        extra: errors,
                    });
                } else {
                    captureMessage('ADD FILE', {
                        extra: errors,
                    });
                }
            });
    });
};

export const getSnackbarText = ({
    items,
    foldersCount = 0,
    filesCount = 0,
    albumsCount = 0,
    type,
}: {
    items?: { name: string }[];
    foldersCount?: number;
    filesCount?: number;
    albumsCount?: number;
    type: ESnackbarType;
}) => {
    const snackbarItemNameMaxLength = 18;
    const message: ISnackbarWordDeclination = snackbarMessage[type];
    const firstName = items?.[0]?.name ?? '';

    if (albumsCount === 1) {
        return `Альбом «${items?.[0]?.name}» ${message.file}`;
    }

    // Файл «name» скопирован
    if (filesCount === 1 && !foldersCount) {
        const fileName = truncateTextMiddle(firstName, snackbarItemNameMaxLength);
        return `Файл «${fileName}» ${message.file}`;
    }

    // Папка «name» скопирована
    if (foldersCount === 1 && !filesCount) {
        const folderName = truncateTextMiddle(firstName, snackbarItemNameMaxLength);
        return `Папка «${folderName}» ${message.folder}`;
    }

    // Скопировано: 1 файл и 1 папка
    let text = `${message.many}: `;

    if (foldersCount) {
        text += `${foldersCount} ${foldersPlural(foldersCount)}`;
    }

    if (foldersCount && filesCount) {
        text += ' и ';
    }

    if (filesCount) {
        text += `${filesCount} ${filesPlural(filesCount)}`;
    }

    return text;
};

export const getCloneSnackbarText = ({ total, success }): string => {
    const fromText = success !== total ? `: ${success} из ${total}` : '';

    return `Успешно загружено в Облако${fromText}`;
};

export const getGoToAndScrollUrl = (folderId: string, itemHome?: string) => {
    return itemHome
        ? `/home${folderId}${folderId?.slice(-1) === '/' ? '' : '/'}?item=${encodeURIComponent(itemHome)}&action=scroll-to`
        : `/home${folderId}`;
};

export const getSnackbarButton = ({ items, destination, homeIdForScrollTo = '' }) => {
    const itemsCount = items.length;
    const item = itemsCount === 1 ? items?.[0] : undefined;
    const buttonText = item ? `Перейти к ${item.isFolder ? 'папке' : 'файлу'}` : 'Открыть папку';

    const onButtonClick = () => toolbarActions.showInParentFolder({ folderId: destination, itemId: homeIdForScrollTo || item?.home });
    return { buttonText, onButtonClick };
};

export const isFileInCurrentFolder = (path: string) => {
    if (!path) {
        return false;
    }

    const pathName = window.location.pathname;
    const urlEndsWithSlash = pathName.charAt(pathName.length - 1) === '/' ? '/' : '';
    const itemPath = `${folderCloudPath(path)}${urlEndsWithSlash}`;

    return encodeURI(`/home${itemPath}`) === pathName || encodeURI(`/public/${itemPath}`) === pathName;
};

export const getNewItem = ({ item, newId, isMounted = item.isMounted }: { item: any; newId: string; isMounted?: boolean }) => {
    const name = getNameById(newId);
    const parent = getParent(newId);
    const ext = getExtension(name);

    const thumbnails = urlBuilder.getThumb({
        hash: item.hash,
        ext,
        id: newId,
        size: item.size,
        kind: item.kind,
        name,
        path: newId,
        isPublic: false,
        isStock: false,
        dwl_token: null,
        weblink: '',
    });

    const url = urlBuilder.getUrls({
        ext,
        isPublic: false,
        id: newId,
        weblink: '',
        size: item.size,
        kind: item.kind,
        subKind: item.subKind,
        isStock: false,
        name,
        path: newId,
    });

    return {
        ...item,
        url,
        name,
        ext,
        parent,
        ext2: ext,
        id: newId,
        thumbnails,
        home: newId,
        isMounted,
        parentFolder: parent,
        nameWithoutExt: getItemNameWithoutExt(name, ext),
    };
};

export const getIdParams = (storage: EStorageType, id: string) => {
    switch (storage) {
        case EStorageType.r7:
        case EStorageType.feed:
        case EStorageType.alldocuments:
        case EStorageType.home:
        case EStorageType.story:
        case EStorageType.start:
        case EStorageType.search:
        case EStorageType.office:
        case EStorageType.gallery:
        case EStorageType.favorites:
        case EStorageType.recommend:
        case EStorageType.documents:
        case EStorageType.sharedLinks:
        case EStorageType.sharedIncoming:
        case EStorageType.sharedAutodelete:
        case EStorageType.attaches:
        case EStorageType.editor:
            return { home: id };
        case EStorageType.public:
            return { weblink: id };
        case EStorageType.stock:
            return { stock: id };
        case EStorageType.trashbin:
            return { trashbin: id };
        case EStorageType.albums:
            return { id };
        default:
            logger.error('idParams wrong storage');
    }
};
